import React, { useState, useEffect, useRef, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import useEditorManager from "../../../hooks/useEditorManager";
import {
	ANSWERS_CONVERSATION_FORMAT,
	STEP_TYPES
} from "components/Custom/AnswerTypeDialog/constants";
import DialogGoogleMap from "../GoogleMap/DiaglogGoogleMap/DialogGoogleMap";
import ConversationInput from "../ConversationInput/ConversationInput";
import TypologyDocument from "./File/TypologyDocument";
import DialogContact from "../DialogContact";
import DialogSurvey from "../DialogSurvey";
import DialogLink from "../DialogLink";
import SurveyMenu from "./SurveyMenu";
import TypologyLink from "./Link";
import Location from "./Location";
import Contact from "./Contact";
import MenuTypology from "./MenuTypology";
import { useTranslation } from "react-i18next";
import DocumentViewerDialog from "../DocumentViewerDialog/DocumentViewerDialog";

const Typology = ({
	dictionary,
	onchangeInputChange,
	value,
	onChangePath,
	isEditMode,
	contact,
	location,
	link,
	menu,
	attachments,
	typologyType,
	locationRequest,
	language,
	code,
	stepTypeSelected,
	alternativeIndex
}) => {
	const { t } = useTranslation();
	const [typologiesValue, setTypologiesValue] = useState({});
	const [openDialogGoogleMap, setOpenDialogGoogleMap] = useState(false);
	const [openDialogContact, setopenDialogContact] = useState(false);
	const [openDialogLink, setOpenDialogLink] = useState(false);
	const [openDialogSurvey, setOpenDialogSurvey] = useState(false);
	const [contactEdited, setContactEdited] = useState({});
	const [openViewFile, setOpenViewFile] = useState(false);
	const fileInputRef = useRef(null);

	const handleEditorInput = useEditorManager(onChangePath);

	const messageText =
		language === "EN" ? "Please share your location" : "Veuillez partagez votre position";

	const isSolutionType = stepTypeSelected === STEP_TYPES.solution;
	const isEditing = isEditMode || Boolean(typologiesValue.menu);
	const surveyDialogTitle = t(
		isEditing
			? isSolutionType
				? "LABELS.editSolution"
				: "LABELS.editSurvey"
			: isSolutionType
				? "LABELS.addSolution"
				: "LABELS.addSurvey"
	);

	const handleTypeChange = (type, value) => {
		if (type === ANSWERS_CONVERSATION_FORMAT.document) {
			onChangePath("attachments", value["attachments"]);
			setTypologiesValue(value);
		}
		if (type === ANSWERS_CONVERSATION_FORMAT.locationRequest) {
			onChangePath(type, value[type]);
			setTypologiesValue(value);
		} else {
			handleEditorInput(type, value?.[type ?? ""]);
			setTypologiesValue(value);
		}
	};

	const memoizeValue = useMemo(() => typologyType, [typologyType]);
	useEffect(() => {
		setTypologiesValue({});
	}, [memoizeValue]);

	useEffect(() => {
		if (location) setTypologiesValue({ location });
		if (contact) setTypologiesValue({ contact });
		if (link) setTypologiesValue({ link });
		if (menu) setTypologiesValue({ menu });
		if (attachments) setTypologiesValue({ attachments });
		if (locationRequest) setTypologiesValue(locationRequest);
		if (!location && !contact && !link && !menu && !attachments) {
			setTypologiesValue({});
		}
	}, [contact, location, link, menu, alternativeIndex]);

	useEffect(() => {
		if (typologyType === ANSWERS_CONVERSATION_FORMAT.locationRequest) {
			const value = {
				locationRequest: { text: messageText }
			};
			handleTypeChange(ANSWERS_CONVERSATION_FORMAT.locationRequest, value);
			setTypologiesValue(value);
		}
	}, [typologyType]);

	const handleReselectFile = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const isExlusiveTypology = [
		ANSWERS_CONVERSATION_FORMAT.richtext,
		ANSWERS_CONVERSATION_FORMAT.document
	]?.includes(typologyType);

	const isLocation = Boolean(typologiesValue?.location);
	const isContact = Boolean(typologiesValue?.contact);
	const isDocument = Boolean(typologiesValue?.attachments);
	const isLink = Boolean(typologiesValue?.link);
	const isSurvey = Boolean(typologiesValue?.menu);
	const isLocationRequest = Boolean(typologiesValue?.locationRequest);

	const hasTypology =
		isLocation || isContact || isLink || isSurvey || !isExlusiveTypology || isLocationRequest;

	return (
		<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexDirection: "column" }}>
			<Box
				component="div"
				sx={
					isExlusiveTypology
						? {
								border: "1px solid #ccc",
								p: 2,
								minHeight: "150px",
								borderRadius: "4px",
								mt: 2,
								outline: "none",
								flex: 1,
								width: "100%"
							}
						: {}
				}
			>
				{!hasTypology && (
					<ConversationInput
						dictionary={dictionary}
						value={value}
						onchangeInputChange={e => onChangePath(ANSWERS_CONVERSATION_FORMAT.richtext, e)}
					/>
				)}

				{isLocation && (
					<Location
						onClick={() => setOpenDialogGoogleMap(true)}
						position={typologiesValue.location}
						onClose={() => {
							setTypologiesValue({});
							onChangePath(ANSWERS_CONVERSATION_FORMAT.location);
						}}
						isEditMode={isEditMode}
					/>
				)}

				{isContact && (
					<Stack direction="row" spacing={0}>
						<Contact
							onClick={() => {
								setopenDialogContact(true);
								setContactEdited(typologiesValue?.contact);
							}}
							firstName={typologiesValue?.contact?.firstName}
							lastName={typologiesValue?.contact.lastName}
							onClose={() => {
								setTypologiesValue(prev => {
									const newContact = prev?.contact;
									onChangePath(ANSWERS_CONVERSATION_FORMAT.contact, newContact);
									return {
										contact: newContact
									};
								});
							}}
							isEditMode={isEditMode}
						/>
					</Stack>
				)}

				{isLink && (
					<TypologyLink
						onClick={() => setOpenDialogLink(true)}
						title={typologiesValue.link.title}
						description={typologiesValue.link.description}
						link={typologiesValue.link.link}
						onClose={() => {
							setTypologiesValue({});
							onChangePath(ANSWERS_CONVERSATION_FORMAT.link);
						}}
						isEditMode={isEditMode}
					/>
				)}

				{isSurvey && (
					<SurveyMenu
						onClick={() => setOpenDialogSurvey(true)}
						question={typologiesValue.menu.subject}
						options={typologiesValue.menu.choices || []}
						onClose={() => {
							setTypologiesValue({});
							onChangePath(ANSWERS_CONVERSATION_FORMAT.menu);
						}}
						isEditMode={isEditMode}
						stepTypeSelected={stepTypeSelected}
					/>
				)}

				{isDocument && (
					<TypologyDocument
						document={typologiesValue.attachments}
						onClose={() => {
							setTypologiesValue({});
							onChangePath(ANSWERS_CONVERSATION_FORMAT.document);
						}}
						onClick={() => setOpenViewFile(true)}
						isEditMode={isEditMode}
						//onReselect={handleReselectFile}
						code={code}
					/>
				)}
			</Box>

			<Box>
				<MenuTypology
					onTypeChange={(type, value) => {
						handleTypeChange(type, value);
					}}
					fileInputRef={fileInputRef}
					typologyType={typologyType}
					language={language}
					code={code}
					stepTypeSelected={stepTypeSelected}
					menu={typologiesValue.menu}
					onShowFile={() => setOpenViewFile(true)}
				/>
			</Box>

			{openDialogContact && (
				<DialogContact
					contact={contactEdited}
					onAddContact={value => {
						handleTypeChange(ANSWERS_CONVERSATION_FORMAT.contact, {
							[ANSWERS_CONVERSATION_FORMAT.contact]: value
						});
					}}
					onClose={() => {
						setopenDialogContact(false);
						setContactEdited({});
					}}
					isEditMode
				/>
			)}
			{openDialogGoogleMap && (
				<DialogGoogleMap
					position={typologiesValue.location}
					onAddLocation={value => {
						onChangePath(ANSWERS_CONVERSATION_FORMAT.location, value);
						handleTypeChange(ANSWERS_CONVERSATION_FORMAT.location, value);
					}}
					onClose={() => setOpenDialogGoogleMap(false)}
					isEditMode
				/>
			)}
			{openDialogLink && (
				<DialogLink
					link={typologiesValue.link}
					onAddLink={value => {
						handleTypeChange(ANSWERS_CONVERSATION_FORMAT.link, value);
						setTypologiesValue(value);
					}}
					onClose={() => setOpenDialogLink(false)}
					isEditMode
				/>
			)}
			{openDialogSurvey && (
				<DialogSurvey
					menu={typologiesValue.menu}
					onClose={() => setOpenDialogSurvey(false)}
					isEditMode={isEditMode}
					onAddSurvey={value => {
						const choices = value?.menu?.choices?.filter(it => it.label !== "");
						value = { menu: { ...value?.menu, choices } };
						handleTypeChange(ANSWERS_CONVERSATION_FORMAT.menu, value);
						setTypologiesValue(value);
					}}
					title={surveyDialogTitle}
					isSolutionType={isSolutionType}
				/>
			)}
			{openViewFile && (
				<DocumentViewerDialog
					handleClose={() => setOpenViewFile(false)}
					documentUrl={typologiesValue?.attachments?.[0]?.url}
				/>
			)}
		</Box>
	);
};

export default Typology;
